// Media UI — Photos (logo + cover) and Gallery (photos + videos) sections for
// the /edit page. Self-contained: each widget uploads to R2 (media-upload.ts)
// and persists the resulting column immediately (persist-on-upload), independent
// of the main text-field Save button.

import { supabase } from './supabase';
import {
  uploadImage,
  uploadGalleryItem,
  deleteMediaByUrl,
  humanSize,
  MEDIA_LIMITS,
  type ImageKind,
  type GalleryItemType,
  type UploadError,
} from './media-upload';

export interface MediaState {
  logo_url: string | null;
  banner_url: string | null;
  cover_image_url: string | null;
  banner_align_x: number | null;
  banner_align_y: number | null;
  media_urls: string[];
  media_types: GalleryItemType[];
}

export interface MediaSectionsOptions {
  userId: string;
  photosEl: HTMLElement;
  galleryEl: HTMLElement;
  media: MediaState;
}

/** Coerce a raw jsonb value to a string[]. */
export function toStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

/** Coerce raw media_types to the image|video union array. Apps store "photo"/"video". */
export function toMediaTypes(raw: unknown): GalleryItemType[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => (x === 'video' ? 'video' : 'image'));
}

/** Internal 'image'|'video' -> the 'photo'|'video' vocabulary the apps store/read. */
function toDbMediaTypes(types: GalleryItemType[]): string[] {
  return types.map((t) => (t === 'video' ? 'video' : 'photo'));
}

/**
 * Cover focal point. The DB stores banner_align_x/y in Flutter's Alignment range
 * (-1..1, 0 = centre) — the convention the apps + in-app editor use. The focal UI
 * works in 0..1 fractions (CSS object-position + handle placement). Convert at the
 * boundary so a web-set focal point positions the cover correctly in the apps.
 */
function alignToFraction(v: number | null | undefined): number {
  if (v == null || Number.isNaN(v)) return 0.5;
  return Math.min(1, Math.max(0, (v + 1) / 2));
}
function fractionToAlign(f: number): number {
  return Math.min(1, Math.max(-1, f * 2 - 1));
}

async function saveColumns(userId: string, patch: Record<string, unknown>): Promise<string | null> {
  const { error } = await supabase.from('provider_profiles').update(patch).eq('user_id', userId);
  return error ? error.message : null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function errMessage(e: unknown): string {
  const ue = e as UploadError;
  return ue?.message ?? 'Upload failed.';
}

// ============================================================================
// Photos: logo + cover (cover also mirrors to cover_image_url + has focal point)
// ============================================================================

interface ImageSlotConfig {
  kind: ImageKind;
  title: string;
  help: string;
  wide: boolean;
  initialUrl: string | null;
  /** Columns to write on a successful upload. */
  columnsFor: (url: string) => Record<string, unknown>;
  /** Focal-point alignment (cover only). getX/getY are 0..1 display fractions. */
  focal?: {
    getX: () => number;
    getY: () => number;
    onChange: (x: number, y: number) => void;
    columnsForSave: () => Record<string, unknown>;
  };
}

function buildImageSlot(userId: string, cfg: ImageSlotConfig): HTMLElement {
  const slot = el('div', 'media-slot');
  slot.append(el('h4', undefined, cfg.title));
  slot.append(el('p', 'muted small', cfg.help));

  const drop = el('div', `image-drop${cfg.wide ? ' image-drop-wide' : ''}`);
  const preview = el('img', 'image-preview');
  preview.alt = `${cfg.title} preview`;
  if (cfg.initialUrl) preview.src = cfg.initialUrl;
  else preview.style.display = 'none';

  // Focal-point handle (cover only).
  let focalHandle: HTMLElement | null = null;
  const applyFocal = (): void => {
    if (!cfg.focal) return;
    const x = cfg.focal.getX();
    const y = cfg.focal.getY();
    preview.style.objectFit = 'cover';
    preview.style.objectPosition = `${x * 100}% ${y * 100}%`;
    if (focalHandle) {
      focalHandle.style.left = `${x * 100}%`;
      focalHandle.style.top = `${y * 100}%`;
    }
  };
  if (cfg.focal) {
    focalHandle = el('span', 'focal-handle');
    focalHandle.setAttribute('aria-hidden', 'true');
    drop.classList.add('has-focal');
  }

  const input = el('input');
  input.type = 'file';
  input.accept = 'image/jpeg,image/png,image/webp,image/gif';
  input.hidden = true;

  const chooseBtn = el('button', 'secondary small', cfg.initialUrl ? 'Replace' : 'Choose file');
  chooseBtn.type = 'button';

  const status = el('p', 'upload-progress', '');
  status.style.display = 'none';

  drop.append(preview);
  if (focalHandle) drop.append(focalHandle);
  drop.append(input, chooseBtn, status);
  slot.append(drop);

  const setStatus = (msg: string, kind: 'info' | 'ok' | 'error'): void => {
    status.style.display = msg ? 'block' : 'none';
    status.textContent = msg;
    status.classList.toggle('upload-ok', kind === 'ok');
    status.classList.toggle('upload-err', kind === 'error');
  };

  async function handleFile(file: File): Promise<void> {
    const objectUrl = URL.createObjectURL(file);
    preview.src = objectUrl;
    preview.style.display = 'block';
    chooseBtn.disabled = true;
    setStatus('Uploading 0%', 'info');
    try {
      const url = await uploadImage(cfg.kind, file, userId, (frac) => {
        setStatus(`Uploading ${Math.round(frac * 100)}%`, 'info');
      });
      const patch = cfg.columnsFor(url);
      const saveErr = await saveColumns(userId, patch);
      if (saveErr) {
        setStatus(`Uploaded, but saving failed: ${saveErr}`, 'error');
        return;
      }
      preview.src = url;
      chooseBtn.textContent = 'Replace';
      applyFocal();
      setStatus('Saved ✓', 'ok');
    } catch (e) {
      setStatus(errMessage(e), 'error');
    } finally {
      chooseBtn.disabled = false;
      URL.revokeObjectURL(objectUrl);
    }
  }

  chooseBtn.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void handleFile(file);
    input.value = '';
  });
  drop.addEventListener('dragover', (e) => {
    e.preventDefault();
    drop.classList.add('drag-over');
  });
  drop.addEventListener('dragleave', () => drop.classList.remove('drag-over'));
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    drop.classList.remove('drag-over');
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  });

  // Focal-point drag (cover only) — persists banner_align_x/y on release.
  if (cfg.focal && focalHandle) {
    applyFocal();
    let dragging = false;
    const toFraction = (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = preview.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (clientX - rect.left) / Math.max(1, rect.width)));
      const y = Math.min(1, Math.max(0, (clientY - rect.top) / Math.max(1, rect.height)));
      return { x, y };
    };
    const onMove = (clientX: number, clientY: number): void => {
      const { x, y } = toFraction(clientX, clientY);
      cfg.focal!.onChange(x, y);
      applyFocal();
    };
    drop.addEventListener('pointerdown', (e) => {
      if (!preview.src || preview.style.display === 'none') return;
      // Only start a focal drag on the image itself, not the buttons.
      if (e.target === chooseBtn) return;
      dragging = true;
      drop.setPointerCapture(e.pointerId);
      onMove(e.clientX, e.clientY);
    });
    drop.addEventListener('pointermove', (e) => {
      if (dragging) onMove(e.clientX, e.clientY);
    });
    const endDrag = async (): Promise<void> => {
      if (!dragging) return;
      dragging = false;
      const saveErr = await saveColumns(userId, cfg.focal!.columnsForSave());
      setStatus(saveErr ? `Focal save failed: ${saveErr}` : 'Focal point saved ✓', saveErr ? 'error' : 'ok');
    };
    drop.addEventListener('pointerup', () => void endDrag());
    drop.addEventListener('pointercancel', () => void endDrag());
  }

  return slot;
}

// ============================================================================
// Gallery: up to 5 photos/videos
// ============================================================================

function buildGallery(userId: string, state: MediaState): HTMLElement {
  const wrap = el('div', 'media-slot');
  wrap.append(el('h4', undefined, 'Gallery — photos & videos'));
  const help = el('p', 'muted small');
  help.append(
    document.createTextNode('Show your space — the dining room, the workshop, a happy customer. '),
  );
  const tipsLink = el('a', undefined, 'Tips for great videos →');
  tipsLink.href = '#/help/recording-tips';
  tipsLink.target = '_blank';
  tipsLink.rel = 'noopener';
  help.append(tipsLink);
  wrap.append(help);

  const grid = el('div', 'gallery-grid');
  const addCard = el('div', 'upload-cards');
  wrap.append(grid, addCard);

  const addInput = el('input');
  addInput.type = 'file';
  addInput.accept = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm';
  addInput.multiple = true;
  addInput.hidden = true;
  wrap.append(addInput);

  const persist = async (): Promise<string | null> => {
    if (state.media_urls.length !== state.media_types.length) {
      return 'Internal error: media arrays out of sync — not saving.';
    }
    return saveColumns(userId, {
      media_urls: state.media_urls,
      media_types: toDbMediaTypes(state.media_types),
    });
  };

  const renderGrid = (): void => {
    grid.innerHTML = '';
    state.media_urls.forEach((url, idx) => {
      const type = state.media_types[idx] ?? 'image';
      const item = el('div', 'gallery-item');
      if (type === 'video') {
        const v = el('video');
        v.src = url;
        v.controls = true;
        v.preload = 'metadata';
        item.append(v);
      } else {
        const img = el('img');
        img.src = url;
        img.alt = 'Gallery item';
        item.append(img);
      }
      const remove = el('button', 'remove-btn', '×');
      remove.type = 'button';
      remove.title = 'Remove';
      remove.setAttribute('aria-label', 'Remove item');
      remove.addEventListener('click', () => void removeAt(idx));
      item.append(remove);
      grid.append(item);
    });
    renderAddTile();
  };

  const renderAddTile = (): void => {
    const existingTile = grid.querySelector('.gallery-add-tile');
    if (existingTile) existingTile.remove();
    if (state.media_urls.length >= MEDIA_LIMITS.maxGalleryItems) return;
    const tile = el('button', 'gallery-add-tile');
    tile.type = 'button';
    tile.innerHTML = '<span>+</span>Add';
    tile.addEventListener('click', () => addInput.click());
    grid.append(tile);
  };

  async function removeAt(idx: number): Promise<void> {
    const url = state.media_urls[idx];
    state.media_urls.splice(idx, 1);
    state.media_types.splice(idx, 1);
    renderGrid();
    const saveErr = await persist();
    if (saveErr) {
      window.alert(saveErr);
      return;
    }
    if (url) void deleteMediaByUrl(url);
  }

  function makeUploadCard(file: File): {
    card: HTMLElement;
    setProgress: (frac: number) => void;
    controller: AbortController;
    done: (msg: string, ok: boolean) => void;
  } {
    const controller = new AbortController();
    const card = el('div', 'upload-card');
    const meta = el('div', 'upload-card-meta');
    meta.append(el('div', 'filename', file.name));
    meta.append(el('div', 'muted small', humanSize(file.size)));
    const bar = el('div', 'progress-bar');
    const fill = el('div', 'progress-fill');
    bar.append(fill);
    meta.append(bar);
    const txt = el('div', 'muted small', 'Starting…');
    meta.append(txt);
    const cancel = el('button', 'link', 'Cancel');
    cancel.type = 'button';
    cancel.addEventListener('click', () => controller.abort());
    card.append(meta, cancel);
    return {
      card,
      controller,
      setProgress: (frac): void => {
        fill.style.width = `${Math.round(frac * 100)}%`;
        txt.textContent = `${Math.round(frac * 100)}%`;
      },
      done: (msg, ok): void => {
        cancel.remove();
        txt.textContent = msg;
        txt.classList.toggle('upload-ok', ok);
        txt.classList.toggle('upload-err', !ok);
        if (ok) setTimeout(() => card.remove(), 1200);
      },
    };
  }

  async function uploadOne(file: File): Promise<void> {
    if (state.media_urls.length >= MEDIA_LIMITS.maxGalleryItems) {
      window.alert(`Gallery is full (max ${MEDIA_LIMITS.maxGalleryItems} items). Remove one first.`);
      return;
    }
    const ui = makeUploadCard(file);
    addCard.append(ui.card);
    try {
      const { url, type } = await uploadGalleryItem(file, userId, ui.setProgress, ui.controller.signal);
      state.media_urls.push(url);
      state.media_types.push(type);
      const saveErr = await persist();
      if (saveErr) {
        // Roll back local state if the DB write failed.
        state.media_urls.pop();
        state.media_types.pop();
        ui.done(`Save failed: ${saveErr}`, false);
        return;
      }
      ui.done('Added ✓', true);
      renderGrid();
    } catch (e) {
      const ue = e as UploadError;
      ui.done(ue.aborted ? 'Cancelled' : errMessage(e), false);
    }
  }

  addInput.addEventListener('change', () => {
    const files = Array.from(addInput.files ?? []);
    addInput.value = '';
    // Sequential uploads — simpler progress, no bandwidth starvation.
    void files.reduce<Promise<void>>((chain, f) => chain.then(() => uploadOne(f)), Promise.resolve());
  });

  renderGrid();
  return wrap;
}

// ============================================================================
// Entry point
// ============================================================================

export function initMediaSections(opts: MediaSectionsOptions): void {
  const { userId, photosEl, galleryEl, media } = opts;

  photosEl.innerHTML = '';
  const heading = el('h2', 'field-section-title', 'Photos');
  photosEl.append(heading);

  photosEl.append(
    buildImageSlot(userId, {
      kind: 'logo',
      title: 'Logo',
      help: 'Square image. A PNG with a transparent background works best.',
      wide: false,
      initialUrl: media.logo_url,
      columnsFor: (url) => ({ logo_url: url }),
    }),
  );

  // Cover/banner: one wide image; mirrors to cover_image_url for the Flutter
  // app + future stop-image wiring. Focal point persists to banner_align_x/y.
  photosEl.append(
    buildImageSlot(userId, {
      kind: 'cover',
      title: 'Cover photo',
      help: 'One wide photo (landscape). Drag the dot to choose the focal point.',
      wide: true,
      initialUrl: media.cover_image_url ?? media.banner_url,
      columnsFor: (url) => ({ banner_url: url, cover_image_url: url }),
      focal: {
        getX: () => alignToFraction(media.banner_align_x),
        getY: () => alignToFraction(media.banner_align_y),
        onChange: (x, y) => {
          media.banner_align_x = fractionToAlign(x);
          media.banner_align_y = fractionToAlign(y);
        },
        columnsForSave: () => ({
          banner_align_x: media.banner_align_x,
          banner_align_y: media.banner_align_y,
        }),
      },
    }),
  );

  galleryEl.innerHTML = '';
  galleryEl.append(buildGallery(userId, media));
}
